import React from 'react';
import '@/styles/confirm-dialog.css';

/**
 * Mission/Annonce list card — V2 (alignée maquettes officielles)
 * Used in: /app/missions, Dashboard "Missions récentes" widget
 *
 * Props:
 *   - route:    "Paris → Dakar"
 *   - subline:  "10 mai · #C2S-2847 · 2 colis"  (string)
 *   - status:   { label: 'En transit', tone: 'gold' | 'teal' | 'blue' | 'red' | 'gray' }
 *   - chips:    [{ icon: Weight, label: '2,4 kg' }, ...] — icon : composant lucide-react
 *   - amount:   number — gros prix en police de titre
 *   - amountUnit: '€' | '€/kg'
 *   - actions:  [{ key, icon: <node>, label, onClick, testid, disabled, tooltip }]
 *   - onClick:  card click handler
 *   - testid:   data-testid
 *   - urgent:   bool — left border red
 *   - confirmBand: { message, onConfirm, onCancel, variant: 'gold'|'red' } — bande inline Option C
 */
export default function MissionListCard({
  route,
  subline,
  status,
  chips = [],
  amount,
  amountUnit = '€',
  actions = [],
  onClick,
  testid,
  urgent = false,
  compact = false,
  confirmBand,
}) {
  return (
    <div
      className={`mlc${compact ? ' mlc--compact' : ''}${urgent ? ' mlc--urgent' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      data-testid={testid}
    >
      <div className="mlc-head">
        <div className="mlc-route-wrap">
          <h3 className="mlc-route">{route}</h3>
          {subline && <div className="mlc-sub">{subline}</div>}
        </div>
        {status && (
          <span className={`mlc-badge mlc-badge--${status.tone || 'gray'}`} data-testid={testid ? `${testid}-status` : undefined}>
            {status.label}
          </span>
        )}
      </div>

      {chips.length > 0 && (
        <div className="mlc-chips">
          {chips.map((c, i) => (
            <span key={c.key || i} className="mlc-chip">
              {c.icon && <c.icon size={13} aria-hidden="true" />}
              <span>{c.label}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mlc-foot">
        <div className="mlc-price" data-testid={testid ? `${testid}-price` : undefined}>
          {amount != null ? `${amount} ${amountUnit}` : '·'}
        </div>
        {actions.length > 0 && (
          <div className="mlc-actions">
            {actions.map((a, i) => (
              <button
                key={a.key || i}
                type="button"
                className={`mlc-icn${a.primary ? ' mlc-icn--primary' : ''}`}
                onClick={(e) => { e.stopPropagation(); if (!a.disabled) a.onClick?.(); }}
                aria-label={a.tooltip || a.label}
                title={a.tooltip || a.label}
                data-testid={a.testid}
                disabled={!!a.disabled}
              >
                {a.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      {confirmBand && (
        <div
          className={`ic-band${confirmBand.variant === 'red' ? ' ic-band--red' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="ic-msg" dangerouslySetInnerHTML={{ __html: confirmBand.message }} />
          <div className="ic-actions">
            <button
              type="button"
              className={`ic-yes${confirmBand.variant === 'red' ? ' ic-yes--red' : ' ic-yes--gold'}`}
              onClick={confirmBand.onConfirm}
            >Oui</button>
            <button type="button" className="ic-no" onClick={confirmBand.onCancel}>Non</button>
          </div>
        </div>
      )}
    </div>
  );
}
